import React, { useCallback, useEffect, useRef, useState } from 'react';
import { previewFontSize } from '../utils/diplomaLayout';
import './DiplomaTemplateEditor.css';

const FIELD_META = [
  { key: 'speaker', label: 'Спикер', sample: 'Иванов Иван Иванович' },
  { key: 'date', label: 'Дата', sample: '01.07.2026' },
  { key: 'participant_name', label: 'ФИО участника', sample: 'Петрова Анна Сергеевна' },
  { key: 'diploma_number', label: 'Номер диплома', sample: 'DIP-A1B2C3D4' },
  { key: 'topic', label: 'Тема', sample: 'Современные подходы в эстетической медицине и коррекции возрастных изменений' },
];

const TOPIC_BOX_DEFAULTS = { box_left: 10, box_right: 90, box_bottom: 96, y: 78 };

function normalizeTopicCfg(cfg = {}) {
  const merged = { ...TOPIC_BOX_DEFAULTS, ...cfg };
  let top = Number(merged.y);
  let bottom = Number(merged.box_bottom);
  let left = Number(merged.box_left);
  let right = Number(merged.box_right);
  if (Number.isNaN(top)) top = TOPIC_BOX_DEFAULTS.y;
  if (Number.isNaN(bottom)) bottom = TOPIC_BOX_DEFAULTS.box_bottom;
  if (Number.isNaN(left)) left = TOPIC_BOX_DEFAULTS.box_left;
  if (Number.isNaN(right)) right = TOPIC_BOX_DEFAULTS.box_right;
  if (right <= left + 3) right = Math.min(98, left + 40);
  if (bottom <= top + 4) bottom = Math.min(98, top + 12);
  return { ...merged, y: top, box_bottom: bottom, box_left: left, box_right: right };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const DiplomaTemplateEditor = ({ imageUrl, layout, fontColor, onLayoutChange, onFontColorChange }) => {
  const containerRef = useRef(null);
  const [activeField, setActiveField] = useState('participant_name');
  const [dragging, setDragging] = useState(null);
  const [canvasWidth, setCanvasWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const updateWidth = () => setCanvasWidth(node.clientWidth || 0);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [imageUrl]);

  const fieldFontSize = (size) => previewFontSize(size, canvasWidth);

  const updateField = useCallback((key, patch) => {
    onLayoutChange({
      ...layout,
      [key]: { ...(layout?.[key] || {}), ...patch },
    });
  }, [layout, onLayoutChange]);

  const positionFromEvent = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clamp(((clientX - rect.left) / rect.width) * 100, 2, 98);
    const y = clamp(((clientY - rect.top) / rect.height) * 100, 2, 98);
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const pos = positionFromEvent(e.clientX, e.clientY);
      if (!pos) return;
      if (dragging === 'topic') {
        const cfg = normalizeTopicCfg(layout?.topic || {});
        let boxBottom = cfg.box_bottom;
        if (boxBottom <= pos.y + 4) {
          boxBottom = Math.min(98, pos.y + 12);
        }
        updateField('topic', { y: pos.y, box_bottom: boxBottom });
        return;
      }
      updateField(dragging, pos);
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, layout, positionFromEvent, updateField]);

  const activeCfg = layout?.[activeField] || {};
  const topicCfg = normalizeTopicCfg(layout?.topic || {});
  const topicEnabled = topicCfg.enabled !== false;

  return (
    <div className="dte-root">
      <div className="dte-canvas-wrap" ref={containerRef}>
        {imageUrl ? (
          <img src={imageUrl} alt="Шаблон диплома" className="dte-image" draggable={false} />
        ) : (
          <div className="dte-image-placeholder">Загрузите изображение шаблона</div>
        )}

        {topicEnabled && (
          <div
            className={`dte-topic-box ${activeField === 'topic' ? 'active' : ''}`}
            style={{
              left: `${topicCfg.box_left ?? 10}%`,
              top: `${topicCfg.y ?? 78}%`,
              width: `${Math.max(5, (topicCfg.box_right ?? 90) - (topicCfg.box_left ?? 10))}%`,
              height: `${Math.max(5, (topicCfg.box_bottom ?? 96) - (topicCfg.y ?? 78))}%`,
            }}
          />
        )}

        {FIELD_META.map((field) => {
          const cfg = field.key === 'topic'
            ? topicCfg
            : (layout?.[field.key] || {});
          if (field.key === 'topic' && cfg.enabled === false) return null;
          if (cfg.enabled === false && field.key !== 'topic') return null;
          const isActive = activeField === field.key;
          const isTopic = field.key === 'topic';

          if (isTopic) {
            return (
              <div
                key={field.key}
                className={`dte-topic-text ${isActive ? 'active' : ''}`}
                style={{
                  left: `${cfg.box_left ?? 10}%`,
                  top: `${cfg.y ?? 78}%`,
                  width: `${Math.max(5, (cfg.box_right ?? 90) - (cfg.box_left ?? 10))}%`,
                  maxHeight: `${Math.max(5, (cfg.box_bottom ?? 96) - (cfg.y ?? 78))}%`,
                  color: fontColor || '#1a1a1a',
                  fontSize: fieldFontSize(cfg.font_size || 18),
                  textAlign: cfg.align || 'center',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setActiveField(field.key);
                  setDragging(field.key);
                }}
                onClick={() => setActiveField(field.key)}
              >
                {field.sample}
              </div>
            );
          }

          return (
            <button
              key={field.key}
              type="button"
              className={`dte-marker ${isActive ? 'active' : ''}`}
              style={{
                left: `${cfg.x ?? 50}%`,
                top: `${cfg.y ?? 50}%`,
                color: fontColor || '#1a1a1a',
                fontSize: fieldFontSize(cfg.font_size || 20),
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveField(field.key);
                setDragging(field.key);
              }}
              onClick={() => setActiveField(field.key)}
            >
              {field.sample}
            </button>
          );
        })}
      </div>

      <div className="dte-sidebar">
        <div className="dte-sidebar-title">Поля на дипломе</div>
        <div className="dte-field-list">
          {FIELD_META.map((field) => (
            <button
              key={field.key}
              type="button"
              className={`dte-field-btn ${activeField === field.key ? 'active' : ''}`}
              onClick={() => setActiveField(field.key)}
            >
              {field.label}
            </button>
          ))}
        </div>

        <div className="dte-controls">
          <label className="dte-control">
            <span>Цвет текста</span>
            <input
              type="color"
              value={fontColor || '#1a1a1a'}
              onChange={(e) => onFontColorChange(e.target.value)}
            />
          </label>

          <label className="dte-control">
            <span>Размер шрифта</span>
            <input
              type="range"
              min="12"
              max="48"
              value={activeCfg.font_size || 20}
              onChange={(e) => updateField(activeField, { font_size: Number(e.target.value) })}
            />
            <span className="dte-range-value">{activeCfg.font_size || 20}px</span>
          </label>

          <label className="dte-control">
            <span>Выравнивание</span>
            <select
              value={activeCfg.align || 'center'}
              onChange={(e) => updateField(activeField, { align: e.target.value })}
            >
              <option value="left">Слева</option>
              <option value="center">По центру</option>
              <option value="right">Справа</option>
            </select>
          </label>

          {activeField === 'topic' && (
            <>
              <label className="dte-checkbox">
                <input
                  type="checkbox"
                  checked={activeCfg.enabled !== false}
                  onChange={(e) => updateField('topic', { enabled: e.target.checked })}
                />
                <span>Показывать тему на дипломе</span>
              </label>

              <div className="dte-box-title">Рамка темы (% от картинки)</div>

              <label className="dte-control">
                <span>Верх (начало текста)</span>
                <input
                  type="range"
                  min="5"
                  max="90"
                  value={topicCfg.y ?? 78}
                  onChange={(e) => updateField('topic', { y: Number(e.target.value) })}
                />
                <span className="dte-range-value">{topicCfg.y ?? 78}%</span>
              </label>

              <label className="dte-control">
                <span>Левая граница</span>
                <input
                  type="range"
                  min="2"
                  max="48"
                  value={topicCfg.box_left ?? 10}
                  onChange={(e) => {
                    const left = Number(e.target.value);
                    const right = Math.max(left + 5, topicCfg.box_right ?? 90);
                    updateField('topic', { box_left: left, box_right: right });
                  }}
                />
                <span className="dte-range-value">{topicCfg.box_left ?? 10}%</span>
              </label>

              <label className="dte-control">
                <span>Правая граница</span>
                <input
                  type="range"
                  min="52"
                  max="98"
                  value={topicCfg.box_right ?? 90}
                  onChange={(e) => {
                    const right = Number(e.target.value);
                    const left = Math.min(right - 5, topicCfg.box_left ?? 10);
                    updateField('topic', { box_right: right, box_left: left });
                  }}
                />
                <span className="dte-range-value">{topicCfg.box_right ?? 90}%</span>
              </label>

              <label className="dte-control">
                <span>Нижняя граница</span>
                <input
                  type="range"
                  min={(topicCfg.y ?? 78) + 5}
                  max="98"
                  value={topicCfg.box_bottom ?? 96}
                  onChange={(e) => updateField('topic', { box_bottom: Number(e.target.value) })}
                />
                <span className="dte-range-value">{topicCfg.box_bottom ?? 96}%</span>
              </label>

              <p className="dte-hint">
                Длинная тема переносится на новые строки внутри рамки и не выходит за левую, правую и нижнюю границы.
              </p>
            </>
          )}

          {activeField !== 'topic' && (
            <p className="dte-hint">Перетащите текст на превью, чтобы настроить позицию поля.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiplomaTemplateEditor;
